import { useState } from 'react'
import ApiService from '@services/api'

const LABELS = {
  command: 'Comando judicial',
  responsibleParty: 'Parte responsável',
  proceduralAct: 'Ato processual',
  explicitDeadline: 'Prazo expresso',
  temporalMilestone: 'Marco temporal',
  court: 'Tribunal',
  area: 'Área',
  regime: 'Regime',
  microsystem: 'Microssistema',
  documentNature: 'Natureza documental',
}

const VERDICTS = { AGREES: 'IA concorda com a análise', DIVERGES: 'IA identificou divergências', INSUFFICIENT_DATA: 'Dados insuficientes para confirmar' }
const STATES = { COMPLETED: 'Concluída', FAILED: 'Falhou', NOT_SAMPLED: 'Fora da amostra', UNAVAILABLE_CONFIGURATION: 'Provedor não configurado', UNAVAILABLE: 'Indisponível', DISABLED: 'Desativada' }
const UNITS = { DAYS: 'dias', BUSINESS_DAYS: 'dias úteis', CALENDAR_DAYS: 'dias corridos', HOURS: 'horas', MONTHS: 'meses', YEARS: 'anos', NO_DEADLINE: 'sem prazo' }

function getField(source, camelKey, pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1), fallback = undefined) {
  return source?.[camelKey] ?? source?.[pascalKey] ?? fallback
}

function formatDate(value) {
  if (!value) return 'Não sugerida'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'UTC' }).format(date)
}

function ReviewEvidence({ item }) {
  const evidence = getField(item, 'evidence', 'Evidence', [])
  return evidence.map((span, index) => <blockquote key={index}>{getField(span, 'text')}</blockquote>)
}

function DeadlineReview({ review }) {
  if (!review) return null
  const verdict = getField(review, 'verdict')
  const divergences = getField(review, 'divergences', 'Divergences', [])
  const suggestions = getField(review, 'suggestions', 'Suggestions', [])
  return (
    <div className="deadline-task-list">
      <p><strong>{VERDICTS[verdict] || verdict}</strong></p>
      <p>{getField(review, 'summary')}</p>
      {divergences.map((item, index) => (
        <article className="deadline-task-item" key={`divergence-${index}`}>
          <h4>Divergência: {LABELS[getField(item, 'field')] || getField(item, 'field')}</h4>
          <p>Motor: {getField(item, 'deterministicValue') || 'Não informado'}</p>
          <p>IA: {getField(item, 'suggestedValue') || 'Não informado'}</p>
          <p>{getField(item, 'reason')}</p>
          <ReviewEvidence item={item} />
        </article>
      ))}
      {suggestions.length > 0 && <p>As alternativas abaixo são sugestões da IA para revisão humana. Os prazos e atividades do motor continuam apresentados separadamente.</p>}
      {suggestions.map((item, index) => (
        <article className="deadline-task-item" key={`suggestion-${index}`}>
          <div className="deadline-task-header">
            <h4>{getField(item, 'suggestedAction')}</h4>
            <span className="status-badge is-warning">Sugestão da IA</span>
          </div>
          <dl className="deadline-task-grid">
            <div><dt>Ação revisada</dt><dd>{getField(item, 'replacesActionCode') || 'Ação adicional sugerida'}</dd></div>
            <div><dt>Parte responsável</dt><dd>{getField(item, 'responsibleParty') || 'Não informada'}</dd></div>
            <div><dt>Prazo sugerido</dt><dd>{getField(item, 'amount') ?? 'Não informado'} {UNITS[getField(item, 'unit')] || getField(item, 'unit')}</dd></div>
            <div><dt>Data inicial sugerida</dt><dd>{formatDate(getField(item, 'initialDate'))}</dd></div>
            <div><dt>Vencimento sugerido</dt><dd>{formatDate(getField(item, 'dueDate'))}</dd></div>
          </dl>
          <p>{getField(item, 'reason')}</p>
          {getField(item, 'legalBasis') && <p>Fundamento indicado pela IA: {getField(item, 'legalBasis')}</p>}
          <ReviewEvidence item={item} />
        </article>
      ))}
    </div>
  )
}

export default function DeadlineAiValidationCard({ result }) {
  const validation = getField(result, 'aiValidation')
  const audit = getField(result, 'audit')
  const [sending, setSending] = useState(false)
  const [feedback, setFeedback] = useState(null)

  if (!validation) return null

  const state = getField(validation, 'state', 'State', 'DISABLED')
  const comparisons = getField(validation, 'comparisons', 'Comparisons', [])
  const warnings = getField(validation, 'warnings', 'Warnings', [])
  const review = getField(validation, 'review')
  const provider = getField(validation, 'provider')
  const model = getField(validation, 'model')
  const simulationId = getField(audit, 'simulationId')
  const isCompleted = String(state).toUpperCase() === 'COMPLETED'

  const submitFeedback = async (decision) => {
    if (!simulationId) return
    setSending(true)
    try {
      const response = await ApiService.submitDeadlineAiValidationFeedback(simulationId, { decision })
      setFeedback({ simulationId, text: response.success ? 'Feedback de qualidade registrado.' : (response.error || 'Não foi possível registrar o feedback.') })
    } catch {
      setFeedback({ simulationId, text: 'Não foi possível registrar o feedback.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="rules-card deadline-ai-review-card">
      <div className="rules-card-header">
        <div>
          <h3>Revisão da análise por IA</h3>
          <p>{getField(validation, 'validationMode') === 'BLIND_EXTRACTION'
            ? 'Resultado histórico de comparação da extração com o motor.'
            : 'A IA revisa a análise concluída pelo motor e apresenta concordâncias, divergências e sugestões para conferência.'}</p>
          {provider && <small>{provider}{model ? ` · ${model}` : ''}</small>}
        </div>
        <span className="status-badge">{STATES[state] || state}</span>
      </div>
      <div className="rules-card-body">
        {!isCompleted ? <p>{warnings[0] || 'A validação não foi executada para esta simulação.'}</p> : (
          <>
            <DeadlineReview review={review} />
            <div className="deadline-evidence-list">
              {comparisons.map((item, index) => (
                <div className="deadline-evidence-item" key={`${getField(item, 'field')}-${index}`}>
                  <strong>{LABELS[getField(item, 'field')] || getField(item, 'field')}: {getField(item, 'status')}</strong>
                  <span>Motor: {getField(item, 'deterministicValue') || 'não identificado'} · IA: {getField(item, 'aiValue') || 'não identificado'}</span>
                </div>
              ))}
            </div>
            {warnings.length ? <p>{warnings.join(' ')}</p> : null}
            <div className="deadline-form-actions">
              <button type="button" className="btn btn-outline" disabled={sending} onClick={() => submitFeedback('ENGINE_CORRECT')}>Motor correto</button>
              <button type="button" className="btn btn-outline" disabled={sending} onClick={() => submitFeedback('AI_CORRECT')}>IA correta</button>
              <button type="button" className="btn btn-outline" disabled={sending} onClick={() => submitFeedback('BOTH_INCORRECT')}>Ambos incorretos</button>
              <button type="button" className="btn btn-outline" disabled={sending} onClick={() => submitFeedback('INSUFFICIENT_DATA')}>Dados insuficientes</button>
            </div>
            {feedback?.simulationId === simulationId ? <small role="status">{feedback.text}</small> : null}
          </>
        )}
      </div>
    </section>
  )
}
